import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState, useRef } from "react";
import Sidebar from "../../components/common/Sidebar.jsx";
import { USER_NAV_ITEMS } from "../../config/userNavConfig.js";
import { authService, chatPageService } from "../../services/index.js";
import { transformMenuData } from "../../utils/menuTransform.js";
import ShareChatModal from "../../components/common/ShareChatModal.jsx";
import { Menu } from "lucide-react";

export default function UserLayout({ darkMode, setDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [vw, setVw] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setVw(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  const isMobile = vw < 768;

  // ── Sidebar collapsed + resizable ─────────────────────────────────────────
  const COLLAPSED_W = 64;
  const MIN_W = 180;
  const MAX_W = 480;
  const DEFAULT_W = 256;
  const [collapsed, setCollapsed] = useState(vw < 1100);
  const [sidebarW, setSidebarW] = useState(DEFAULT_W);
  const wrapperRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startW: 0 });

  // Auto-collapse on narrow viewport
  const prevVw = useRef(vw);
  useEffect(() => {
    if (prevVw.current >= 1100 && vw < 1100) setCollapsed(true);
    if (prevVw.current < 1100 && vw >= 1100) setCollapsed(false);
    prevVw.current = vw;
  }, [vw]);

  const effectiveW = collapsed ? COLLAPSED_W : sidebarW;

  const onDragStart = (e) => {
    if (collapsed) return;
    e.preventDefault();
    const el = wrapperRef.current;
    if (!el) return;
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startW = el.getBoundingClientRect().width;
    dragRef.current = { active: true, startX, startW };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    el.style.transition = "none";

    const onMove = (ev) => {
      if (!dragRef.current.active) return;
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const newW = Math.max(MIN_W, Math.min(MAX_W, dragRef.current.startW + (x - dragRef.current.startX)));
      el.style.width = newW + "px";
    };

    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      el.style.transition = "";
      const finalW = Math.round(el.getBoundingClientRect().width);
      setSidebarW(Math.max(MIN_W, Math.min(MAX_W, finalW)));
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => { if (!isMobile) setMobileSidebarOpen(false); }, [isMobile]);
  useEffect(() => { setMobileSidebarOpen(false); }, [location.pathname]);
  useEffect(() => {
    const handler = () => setMobileSidebarOpen(false);
    document.addEventListener("sidebar-close", handler);
    return () => document.removeEventListener("sidebar-close", handler);
  }, []);

  const [activeUseCase, setActiveUseCase] = useState(() => {
    const saved = sessionStorage.getItem("activeUseCase");
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (activeUseCase) sessionStorage.setItem("activeUseCase", JSON.stringify(activeUseCase));
    else sessionStorage.removeItem("activeUseCase");
  }, [activeUseCase]);

  const navItems = useMemo(() => {
    const menuData = localStorage.getItem("menuData");
    if (menuData) {
      try { return transformMenuData(JSON.parse(menuData)); }
      catch (e) { console.error("Failed to parse menu data:", e); }
    }
    return USER_NAV_ITEMS;
  }, []);

  const [, setActiveTab] = useState("");
  const pathSegment = location.pathname.split("/")[2] || "";
  const tabMap = { "": "workspaceSelector", chat: "chat", history: "history", settings: "settings" };
  const derivedTab = tabMap[pathSegment] || "chat";
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatRenaming, setIsChatRenaming] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  const fetchChatHistory = async () => {
    try {
      const response = await chatPageService.fetchChatHistory();
      const list = response?.data || response || [];
      setChatHistory(Array.isArray(list) ? list : []);
    } catch { setChatHistory([]); }
  };

  const handlePin    = async (id, isPinned) => { try { await chatPageService.pinChat(id, !isPinned); fetchChatHistory(); } catch {} };
  const handleRename = async (id, title)   => { try { await chatPageService.renameChat(id, title); fetchChatHistory(); } catch {} };
  const handleDelete = async (id)          => { try { await chatPageService.deleteChat(id); fetchChatHistory(); } catch {} };
  const handleArchive = async (id, isArchived = true) => { try { await chatPageService.archiveChat(id, isArchived); fetchChatHistory(); } catch {} };
  const handleShare  = async (id) => {
    try { const res = await chatPageService.shareChat(id); const url = res?.url || res?.data?.url; if (url) setShareUrl(url); } catch {}
  };

  useEffect(() => {
    chatPageService.fetchChatHistory()
      .then(r => { const l = r?.data || r || []; setChatHistory(Array.isArray(l) ? l : []); })
      .catch(() => setChatHistory([]));
  }, []);

  const handleNavChange    = (tabId) => setActiveTab(tabId);
  const handleUseCasesClick = () => {
    sessionStorage.removeItem("activeUseCase");
    setActiveUseCase(null);
    setActiveTab("workspaceSelector");
    navigate("/user");
    fetchChatHistory();
  };
  const handleLogout = async() => { 
    await authService.logout();
    sessionStorage.clear();
    localStorage.removeItem("refreshToken"); 
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("groupId");
    localStorage.removeItem("expiresAtUtc");
    localStorage.removeItem("menuData");
    localStorage.removeItem("user");
    localStorage.removeItem("gxp_frontend_api_logs");
    navigate("/login"); 
  };

  const storedUser = JSON.parse(localStorage.getItem("user"));
  const username = storedUser?.email
    ? storedUser.email.split("@")[0].replace(".", " ").replace(/\b\w/g, c => c.toUpperCase())
    : "User";
  const user = { username, email: storedUser?.email || "user@example.com" };

  return (
    <div className="flex h-full w-full bg-[#e8edf3] dark:bg-[#0f172a] transition-colors duration-300">

      {/* Sidebar wrapper — desktop only, controls width */}
      {!isMobile && (
        <div
          ref={wrapperRef}
          className="relative flex-shrink-0 h-full transition-[width] duration-200"
          style={{ width: effectiveW }}
        >
          <Sidebar
            navItems={navItems}
            activeTab={derivedTab}
            setActiveTab={setActiveTab}
            setActiveUseCase={setActiveUseCase}
            activeUseCase={activeUseCase}
            onNavChange={handleNavChange}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            user={user}
            handleLogout={handleLogout}
            chatHistory={chatHistory}
            isSidebarOpen={true}
            onUseCasesClick={handleUseCasesClick}
            onPin={handlePin}
            onRename={handleRename}
            onDelete={handleDelete}
            onArchive={handleArchive}
            onShare={handleShare}
            onRenamingChange={setIsChatRenaming}
            collapsed={collapsed}
            onCollapsedChange={(c) => {
              setCollapsed(c);
              if (!c) setSidebarW(DEFAULT_W);
            }}
          />
          {/* Drag handle — only when expanded */}
          {!collapsed && (
            <div
              onMouseDown={onDragStart}
              onTouchStart={onDragStart}
              className="absolute right-0 top-0 bottom-0 w-2 z-50 cursor-col-resize group"
              style={{ touchAction: "none" }}
            >
              <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-slate-200 dark:bg-slate-700 group-hover:w-1 group-hover:bg-[#1d57a5] transition-all duration-150" />
            </div>
          )}
        </div>
      )}

      {/* Mobile sidebar */}
      {isMobile && (
        <Sidebar
          navItems={navItems}
          activeTab={derivedTab}
          setActiveTab={setActiveTab}
          setActiveUseCase={setActiveUseCase}
          activeUseCase={activeUseCase}
          onNavChange={handleNavChange}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          user={user}
          handleLogout={handleLogout}
          chatHistory={chatHistory}
          isSidebarOpen={mobileSidebarOpen}
          onUseCasesClick={handleUseCasesClick}
          onPin={handlePin}
          onRename={handleRename}
          onDelete={handleDelete}
          onArchive={handleArchive}
          onShare={handleShare}
          onRenamingChange={setIsChatRenaming}
          collapsed={false}
          onCollapsedChange={() => {}}
        />
      )}

      <main className="flex-1 flex flex-col h-full overflow-hidden min-w-0">
        {isMobile && (
          <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
            <button className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" onClick={() => setMobileSidebarOpen(true)}>
              <Menu size={20} />
            </button>
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">User Portal</span>
          </div>
        )}
        <Outlet context={{ activeUseCase, setActiveUseCase, fetchChatHistory, darkMode, chatHistory, isChatRenaming, onShareChat: handleShare }} />
      </main>

      <ShareChatModal isOpen={!!shareUrl} onClose={() => setShareUrl(null)} shareUrl={shareUrl} />
    </div>
  );
}
